/**
 * Central session-gating helpers (P1 #7).
 *
 * A company in one of these statuses must immediately end the user's session.
 * `pending_verification` is intentionally NOT blocking — those users may sign in
 * while awaiting admin approval; `active` is the normal allowed state.
 */
export const BLOCKING_COMPANY_STATUSES = ['rejected', 'inactive', 'suspended'] as const;

/** True when a loaded company status must force an immediate sign-out. */
export function isBlockedCompanyStatus(status: string | undefined | null): boolean {
  return status != null && (BLOCKING_COMPANY_STATUSES as readonly string[]).includes(status);
}

/**
 * Outcome of the central app-shell access gate.
 * - `allow`         — user may enter the app shell.
 * - `verify-email`  — Firebase email not verified; hold on the verify screen.
 * - `pending`       — company still awaiting admin approval (`pending_verification`).
 * - `blocked`       — company is rejected/inactive/suspended.
 * - `account-error` — email verified but the company document is missing / failed
 *                     to load, so the session has no tenant (`companyId=''`). The
 *                     user must NOT enter the shell with an empty tenant; show a
 *                     recoverable "account problem" screen instead of hard-crashing.
 */
export type AccessDecision = 'allow' | 'verify-email' | 'pending' | 'blocked' | 'account-error';

export interface AccessGateInput {
  /**
   * Admin users authenticate through the DB-based `adminUsers` flow, not Firebase
   * email verification — they must never be gated by this logic. Regular company
   * sessions leave this `false`/undefined.
   */
  isAdmin?: boolean;
  /** Firebase `user.emailVerified`. */
  emailVerified: boolean;
  /**
   * Loaded company document, or `undefined`/`null` when it is missing or failed to
   * load. The gate is evaluated only AFTER the auth + company load has settled
   * (AppLayout shows a spinner while `loading` is true), so an absent company here
   * is a genuine account problem — never a transient in-flight load.
   */
  companyData?: { status?: string | null } | null;
}

/**
 * Pure decision for whether an authenticated session may enter the protected app
 * shell. Evaluation order (most-restrictive wins):
 *   1. Admins always pass (separate auth model).
 *   2. A hard-blocked company state ends access entirely.
 *   3. An unverified email must be verified before anything else.
 *   4. A verified session with NO company document is an account error (no tenant).
 *   5. A company still awaiting admin approval is held on the pending screen.
 *   6. Otherwise access is granted.
 *
 * Keeping this logic pure (no React / Firebase) lets the app shell, and the unit
 * tests, share one source of truth for the gate.
 */
export function evaluateAccess(input: AccessGateInput): AccessDecision {
  if (input.isAdmin) return 'allow';
  const status = input.companyData?.status;
  if (isBlockedCompanyStatus(status)) return 'blocked';
  if (!input.emailVerified) return 'verify-email';
  if (input.companyData == null) return 'account-error';
  if (status === 'pending_verification') return 'pending';
  return 'allow';
}
